import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Page, SectionEyebrow, CTAButton } from "@/components/SiteLayout";
import faqImg from "@/assets/faq-transparent.png";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "Veelgestelde vragen — Hi Coach" },
      { name: "description", content: "Antwoorden op de meest gestelde vragen over Hi Coach Club: lessen, groepen, sportsetje en inschrijving." },
      { property: "og:title", content: "FAQ — Hi Coach" },
      { property: "og:description", content: "Over de lessen & beweging." },
      { property: "og:url", content: "/faq" },
    ],
    links: [{ rel: "canonical", href: "/faq" }],
  }),
  component: FAQ,
});

const groups = [
  { color: "#FF0000", title: "🤸 Over de lessen & beweging", items: [
    { q: "Hoe laat beginnen de lessen precies?", a: "Zowel de peuters- als de kleutergroep sporten op zondag van 09:30 tot 10:30 uur, in Sporthal Portland. De zaal wordt in tweeën gesplitst: peuters in zaaldeel 1, kleuters in zaaldeel 2 — allebei op hetzelfde tijdstip." },
    { q: "Mag ik als ouder blijven kijken naar de les?", a: "Blijven kijken? Je mag zelfs meevoelen en meedoen! Bij de peuters (2-3 jaar) zijn ouders geen toeschouwers langs de zijlijn, maar doen ze gezellig mee op de vloer als 'co-coach'. Bij de kleuters (4-6 jaar) gaan de kinderen zelfstandiger op pad, maar kun je ze prachtig aanmoedigen. Dit is superleuk voor je kind én fantastisch voor de onderlinge band." },
    { q: "Mijn kind is nogal verlegen. Is deze club wel geschikt?", a: "Juist wel! Omdat jij als ouder direct nabij of naast je kind staat, voelt de omgeving meteen veilig en vertrouwd. Daarnaast zorgt onze coach en de kleine groepsgrootte ervoor dat elk kind op zijn eigen tempo kan ontdooien." },
    { q: "Worden de thema's en lessen herhaald, of leert mijn kind steeds iets nieuws?", a: "Het blijft het hele jaar door één grote ontdekkingsreis! Ons totale programma bestaat uit 35 unieke lessen, verdeeld over 5 verschillende thema's. We werken het hele jaar door aan de vaste bewegingsdoelen van de ASM-methode. Juist door herhaling worden kinderen handiger in deze basisbewegingen. Om het uitdagend te houden, verandert de vorm van de lessen wel continu. Denk aan een parcours dat net een andere vorm krijgt, of een oefening waar een extra handeling aan wordt toegevoegd. Zo leert je kind steeds iets nieuws, terwijl de belangrijkste basisbewegingen herhaaldelijk aan bod komen." },
  ]},
  { color: "#1E90FF", title: "🧒 Over de groepen & de groepsgrootte", items: [
    { q: "Hoe groot zijn de groepen bij de club?", a: "We houden de groepen bewust klein voor maximale aandacht. Er komen structureel 10 tot 12 kinderen per groep, aangevuld met incidentele proefleskinderen, tot een absolute limiet van maximaal 12 kinderen op de vloer. Vol is bij ons ook echt vol!" },
    { q: "Wat als mijn kind niet goed mee kan komen in de groep?", a: "Geen zorgen, elk kind ontwikkelt zich op zijn eigen tempo. Onze coaches kijken vooral naar wat jouw kind nodig heeft en aankan. Let op: de parcoursen zijn specifiek afgestemd op de leeftijd (peuters versus kleuters). Mocht een andere groep beter passen, dan kijken we in overleg rustig naar een overstap." },
    { q: "Wat als mijn kind nog niet zindelijk is?", a: "Dat is voor ons geen enkel probleem. Omdat je als ouder zelf ook nabij of in de zaal aanwezig bent, kun je je kind indien nodig tussendoor zelf even verschonen of begeleiden." },
  ]},
  { color: "#8B00FF", title: "📅 Over aanwezigheid & afmelden", items: [
    { q: "Wat als we een keertje op vakantie zijn of mijn kind ziek is?", a: "Dat is geen probleem. Geef het simpelweg even vooraf aan ons door via een snel WhatsApp berichtje, dan weet de coach dat jullie er een zondag niet zijn. Omdat we werken met vaste lespakketten, blijft de plek van jouw kind gewoon gereserveerd." },
  ]},
  { color: "#FF7F00", title: "🎫 Over inschrijving, flyers & proeflessen", items: [
    { q: "Ik heb een flyer ontvangen, hoe werkt de gratis proefles?", a: "Je kunt via de knop 'Gratis proefles' op de website direct een plekje reserveren. We hebben consistent proeflesplekken per les beschikbaar naast onze vaste leden, zodat elk proefleskind alle ruimte en aandacht krijgt om de coach te ontmoeten." },
    { q: "Hoe flexibel is het lespakket en kan mijn kind eerst kennismaken?", a: "Om te kijken of Hi Coach Club bij jouw kind past, starten we altijd met een gratis proefles. Dit is een laagdrempelige manier om kennis te maken. Daarna kies je een lespakket per thema. We werken graag met je samen om je kind te motiveren dit thema helemaal af te ronden. Na afloop beslis je telkens zelf of je stopt of verlengt; je zit dus per gekozen pakket minimaal vast aan de duur van één thema." },
  ]},
  { color: "#00C851", title: "👕 Over kleding, spullen & de locatie", items: [
    { q: "Is het officiële Hi Coach Club sportsetje verplicht?", a: "Ja, het officiële Hi Coach Club sportsetje (T-shirt en broekje) hoort er helemaal bij vanaf de inschrijving. Dit zorgt ervoor dat alle kinderen zich echt onderdeel voelen van de club, net als onze clubvriendjes Sem & Maya." },
    { q: "Waar in Rhoon worden de lessen precies gegeven?", a: "De lessen worden gegeven in Sporthal Portland, De Beurs 41, 3162 WB Rhoon. Deze locatie is uitstekend bereikbaar en er is voldoende gratis parkeergelegenheid direct voor de deur. Wel zo fijn en stressvrij op de zondagochtend!" },
  ]},
];

function FAQ() {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Page>
      <section className="pt-24 pb-10 bg-white">
        <div className="mx-auto max-w-4xl px-6 flex flex-col items-center text-center">
          <div className="flex items-center gap-3 mb-4">
            <span className="block h-1 w-12 rounded-full" style={{ background: "var(--rb-violet)" }} />
            <span className="uppercase tracking-widest text-xs font-extrabold" style={{ color: "var(--rb-violet)" }}>FAQ</span>
            <span className="block h-1 w-12 rounded-full" style={{ background: "var(--rb-violet)" }} />
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">
            Over de <span style={{ color: "var(--rb-violet)" }}>lessen &amp; beweging</span>
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl">
            Staat jouw vraag er niet tussen? Stuur ons een berichtje via WhatsApp, we reageren snel.
          </p>
          <img src={faqImg} alt="Kind met vraagteken" className="mt-8 w-full max-w-xs h-auto" loading="lazy" />
        </div>
      </section>

      <section className="pb-16">
        <div className="mx-auto max-w-3xl px-6 space-y-10">
          {groups.map((g) => (
            <div key={g.title}>
              <div className="rounded-t-2xl px-6 py-3 text-white font-extrabold text-lg" style={{ background: g.color }}>
                {g.title}
              </div>
              <div className="rounded-b-2xl border-x border-b border-border bg-white divide-y divide-border overflow-hidden">
                {g.items.map((it) => {
                  const id = g.title + it.q;
                  const isOpen = open === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setOpen(isOpen ? null : id)}
                      className="w-full text-left"
                    >
                      <div className="flex items-center justify-between px-6 py-4 hover:bg-secondary transition-colors">
                        <span className="font-bold pr-4">{it.q}</span>
                        <ChevronDown className={`shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} style={{ color: g.color }} />
                      </div>
                      <div className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                        <div className="overflow-hidden">
                          <p className="px-6 pb-5 text-muted-foreground">{it.a}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 text-center bg-white">
        <CTAButton />
      </section>
    </Page>
  );
}
